import { Lock } from 'lucide-react';
import type { TimelineBlock, Space } from '../../../types';
import { TIMELINE_CONTENT_RIGHT_PAD, TIMELINE_GUTTER_WIDTH, timeToMinutes, DEFAULT_DURATION, minutesToTime } from '../../../lib/timeline';
import type { ColumnLayout } from '../../../lib/timeline';

interface CrossSpaceTimelineBlockProps {
  block: TimelineBlock;
  currentSpaceId: string | null;
  spaces: Space[];
  toPixel: (minutes: number) => number;
  durationToPx: (duration: number) => number;
  columnLayout?: ColumnLayout;
}

export function CrossSpaceTimelineBlock({ block, currentSpaceId, spaces, toPixel, durationToPx, columnLayout }: CrossSpaceTimelineBlockProps) {
  const startMin = timeToMinutes(block.scheduledStartTime);
  const duration = block.scheduledDuration ?? DEFAULT_DURATION;
  const top = toPixel(startMin);
  const height = Math.max(durationToPx(duration), 24);
  const endMin = startMin + duration;
  const isShort = height < 36;
  const blockSpace = spaces.find((s) => s.id === block.spaceId);
  const isAnonymized = blockSpace?.private ?? false;

  const col = columnLayout?.column ?? 0;
  const totalCols = columnLayout?.totalColumns ?? 1;

  const style: React.CSSProperties = {
    top,
    height,
    ...(totalCols > 1 ? {
      left: `calc(${TIMELINE_GUTTER_WIDTH}px + ${(col / totalCols) * 100}% - ${(col / totalCols) * TIMELINE_GUTTER_WIDTH}px)`,
      width: `calc(${100 / totalCols}% - ${TIMELINE_GUTTER_WIDTH / totalCols}px - ${TIMELINE_CONTENT_RIGHT_PAD / totalCols}px)`,
    } : {
      left: TIMELINE_GUTTER_WIDTH,
      right: TIMELINE_CONTENT_RIGHT_PAD,
    }),
  };

  if (isAnonymized) {
    return (
      <div
        className={`absolute rounded-md overflow-hidden select-none
          bg-text-muted/6 border border-dashed border-border-subtle/30`}
        style={style}
        title="Private Appointment"
      >
        <div className={`px-2 ${isShort ? 'py-0.5 flex items-center gap-1.5' : 'py-1.5'}`}>
          <Lock size={10} className="text-text-muted/50 shrink-0" />
          <span className="text-micro text-text-muted/60 truncate block">Private Appointment</span>
        </div>
      </div>
    );
  }

  const priorityColors: Record<string, string> = {
    p1: 'bg-p1/8',
    p2: 'bg-p2/8',
    p3: 'bg-p3/8',
  };
  const bg = priorityColors[block.priority] ?? priorityColors.p2;

  return (
    <div
      className={`absolute rounded-md overflow-hidden select-none
        ${bg} border border-dashed border-border-subtle/40 opacity-70`}
      style={style}
      title={`${block.title} (${block.scheduledStartTime} – ${minutesToTime(endMin)})`}
    >
      <div className={`px-2 ${isShort ? 'py-0.5 flex items-center gap-1.5' : 'py-1.5'}`}>
        {isShort && <Lock size={9} className="text-text-muted/50 shrink-0" />}
        <span className="text-micro font-medium text-text-primary/70 truncate block">
          {block.title}
        </span>
        {!isShort && (
          <div className="flex items-center gap-1">
            <Lock size={9} className="text-text-muted/50" />
            <span className="text-micro text-text-muted font-mono">
              {block.scheduledStartTime} – {minutesToTime(endMin)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
